import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
  HttpContextToken,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
  timeout,
} from 'rxjs';
import { AranSnackBarService } from '@aran-client/aran-web-ui-toolkit';
import { StorageService } from 'src/app/shared/services/storage/storage.service';
import { StorageKeys } from 'src/app/shared/enums/storage.enum';
import {
  LoginResultType,
  RefreshTokenType,
} from 'src/app/shared/types/auth.types';
import { AuthService } from 'src/app/services/auth/auth.service';
import { NavigationService } from 'src/app/shared/services/navigation/navigation.service';
export const BYPASS_ERROR = new HttpContextToken(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private notificationService: AranSnackBarService,
    private storageService: StorageService,
    private apiService: AuthService,
    private navigation: NavigationService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (req.url.endsWith('/assets/config.json')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error: any) => {
        if (req.context.get(BYPASS_ERROR)) return throwError(() => error);

        if (error instanceof HttpErrorResponse) {
          if (error.status === 0) {
            const errorMessage =
              error?.error?.message ||
              'Something went wrong. Please try again later.';

            this.notificationService.showError(errorMessage, 10000);

            return throwError(() => error);
          }

          switch (error.status) {
            case HttpStatusCode.Unauthorized:
              return this.handle401Error(req, next);
            case HttpStatusCode.BadRequest:
              return this.handle400Error(error);
            case HttpStatusCode.InternalServerError:
              return this.handle500Error(error);
            case HttpStatusCode.Forbidden:
              return this.handle403Error(error);
            case HttpStatusCode.NotFound:
              return this.handle404Error(error);
            default:
              this.notificationService.showError(error.error.message, 10000);
              return throwError(() => error);
          }
        } else {
          this.notificationService.showError(error.message, 10000);
          return throwError(() => error);
        }
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const token: any = this.storageService.get(StorageKeys.AccessToken);

      const refreshTokenBody: RefreshTokenType = {
        refreshToken: token.refreshToken,
      };

      return this.apiService.refreshToken(refreshTokenBody).pipe(
        switchMap((newToken: LoginResultType) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          this.storageService.set(StorageKeys.AccessToken, newToken);

          return next.handle(this.addToken(request, newToken));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.storageService.remove(StorageKeys.AccessToken);

          this.navigation.goToLogin();
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        timeout(1000),
        switchMap((newToken: LoginResultType) => {
          this.storageService.set(StorageKeys.AccessToken, newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((err) => {
          this.storageService.remove(StorageKeys.AccessToken);
          this.navigation.goToLogin();
          return throwError(() => err);
        })
      );
    }
  }

  private handle400Error(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ArrayBuffer) {
      const textDecoder = new TextDecoder();

      try {
        const decodedMessage = textDecoder.decode(error.error);
        const errorObject = JSON.parse(decodedMessage);
        this.notificationService.showError(errorObject.message, 10000);
      } catch (e) {
        this.notificationService.showError('Error occurred', 10000);
      }
    } else if (typeof error.error === 'object') {
      this.notificationService.showError(error.error.message, 10000);
    } else if (typeof error.error === 'string') {
      this.notificationService.showError(error.error, 10000);
    }

    return throwError(() => new Error('Bad request error: ' + error.message));
  }

  private handle500Error(error: HttpErrorResponse): Observable<never> {
    this.notificationService.showError(
      'Internal server error. Please try again later.',
      10000
    );
    return throwError(
      () => new Error('Internal server error: ' + error.message)
    );
  }

  private handle403Error(error: HttpErrorResponse): Observable<never> {
    this.notificationService.showError(
      'Access forbidden. You do not have permission to access this resource.',
      10000
    );
    return throwError(() => new Error('Forbidden error: ' + error.message));
  }

  private handle404Error(error: HttpErrorResponse): Observable<never> {
    this.notificationService.showError(
      error.error
        ? error.error.message
        : 'The requested resource was not found',
      10000
    );
    return throwError(() => new Error('Not Found error: ' + error.message));
  }

  private addToken(request: HttpRequest<any>, token: LoginResultType) {
    let accessToken = token.accessToken.startsWith('Bearer')
      ? token.accessToken
      : `Bearer ${token.accessToken}`;

    return request.clone({
      setHeaders: {
        Authorization: accessToken,
      },
    });
  }
}
